import { FC, useMemo, useRef, useState } from 'react';

import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { toastError } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';

import { BillingCardSection } from './couponDetail/BillingCardSection';
import { CancelConfirmDialog } from './couponDetail/CancelConfirmDialog';
import { CancelSection } from './couponDetail/CancelSection';
import { CouponDetailHeader } from './couponDetail/CouponDetailHeader';
import { CouponShareHistoryModal } from './couponDetail/CouponShareHistoryModal';
import { CouponTable } from './couponDetail/CouponTable';
import { CourtesyMessageModal } from './couponDetail/CourtesyMessageModal';
import { CourtesySection } from './couponDetail/CourtesySection';
import { CrossCouponOverlapBanner } from './couponDetail/CrossCouponOverlapBanner';
import { DisposeConfirmDialog } from './couponDetail/DisposeConfirmDialog';
import { DisposeSection } from './couponDetail/DisposeSection';
import { ExtraRedeemModal } from './couponDetail/ExtraRedeemModal';
import { ExtraFulfillModal, ExtraPayModal } from './ExtraPaymentActionModals';
import { KakaoShareCallbackModal } from './couponDetail/KakaoShareCallbackModal';
import { ManualRefundCancelDialog } from './couponDetail/ManualRefundCancelDialog';
import { PhoneFormUploadModal } from './couponDetail/PhoneFormUploadModal';
import { ProxyDeliverModal } from './couponDetail/ProxyDeliverModal';
import { ReissueSection } from './couponDetail/ReissueSection';
import { ResultBanners } from './couponDetail/ResultBanners';
import { isProxyDeliverEligible } from './couponDetail/constants';
import { CouponDetailModalProps } from './couponDetail/types';
import { useCouponDetailState } from './couponDetail/useCouponDetailState';
import { UsageGuideSendModal } from './couponDetail/UsageGuideSendModal';
import {
  exportDeliveredCouponPhoneExcel,
  getDeliveredCouponPhoneTargets,
  getPhoneFormTargets,
} from '../utils/deliveredCouponPhoneExcel';
import {
  parsePhoneFormWorkbook,
  PhoneFormParseResult,
} from '../utils/phoneFormExcelUpload';

export type { CouponDetailModalProps };

export const CouponDetailModal: FC<CouponDetailModalProps> = (props) => {
  const { onClose } = props;
  const { isAdmin, isGiftAgent } = useAuth();
  const state = useCouponDetailState(props);
  const sortedCoupons = state.sortedCoupons;
  const couponMap = useMemo(() => {
    const map = new Map<string, GiftCouponSummary>();
    for (const c of sortedCoupons) map.set(c._id, c);
    return map;
  }, [sortedCoupons]);
  const canSendCourtesy = isAdmin() || isGiftAgent();

  // 이용안내 일괄 발송 — 주문 단위로, 신청자(수신) 번호가 있는 쿠폰들에 교환·잔액
  // 유의사항(이용안내)을 LMS로 한 번 더 고지한다. (백엔드 엔드포인트 준비 후 동작)
  const [showUsageGuideModal, setShowUsageGuideModal] = useState(false);
  // 백엔드와 동일 기준: 이미 교환(Requested/Delivered)했고 신청자 번호가 있는 쿠폰.
  const usageGuideTargets = useMemo(
    () =>
      sortedCoupons.filter(
        (c) =>
          !!c.recipient?.phoneNumber &&
          (c.redeemStatus === GiftCouponRedeemStatus.Requested ||
            c.redeemStatus === GiftCouponRedeemStatus.Delivered),
      ),
    [sortedCoupons],
  );
  const canSendUsageGuide =
    isAdmin() && !!props.orderId && usageGuideTargets.length > 0;

  // 발송완료 번호 엑셀 — 신청자 번호(나중에받기·번호인증·발송완료)가 있는 쿠폰을
  // 가운데 4자리 마스킹해 내보낸다. 대상이 없으면 버튼 비활성화.
  const deliveredPhoneTargets = useMemo(
    () => getDeliveredCouponPhoneTargets(sortedCoupons),
    [sortedCoupons],
  );
  const canExportDeliveredPhones =
    (isAdmin() || isGiftAgent()) && deliveredPhoneTargets.length > 0;
  // 미발송 포함(번호 기입 폼) — 취소·폐기 제외 전체. 미발송 건이 있을 때만 의미가 있다.
  const phoneFormTargets = useMemo(
    () => getPhoneFormTargets(sortedCoupons),
    [sortedCoupons],
  );
  const canExportPhoneForm =
    (isAdmin() || isGiftAgent()) &&
    phoneFormTargets.length > deliveredPhoneTargets.length;

  // 기입폼 업로드 — 채워 받은 엑셀의 수령번호를 확인번호로 일괄 저장.
  // 저장 엔드포인트가 admin 전용(PATCH /admin/gift-coupon/:id)이라 admin만 노출.
  const phoneFormFileRef = useRef<HTMLInputElement>(null);
  const [phoneFormParsed, setPhoneFormParsed] =
    useState<PhoneFormParseResult | null>(null);
  const canUploadPhoneForm = isAdmin() && sortedCoupons.length > 0;
  const handlePhoneFormFile = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      setPhoneFormParsed(parsePhoneFormWorkbook(buf, sortedCoupons));
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : '엑셀 파일을 읽지 못했습니다.',
      );
    }
  };

  // 대리 일괄발송 — 수령자 신청(번호인증·서명) 없이 관리자가 연락처를 받아 주문의
  // 쿠폰들을 바로 발송완료 처리. (404 안내는 구서버 안전장치)
  const [showProxyDeliverModal, setShowProxyDeliverModal] = useState(false);
  const proxyDeliverTargets = useMemo(
    () => sortedCoupons.filter(isProxyDeliverEligible),
    [sortedCoupons],
  );
  const canProxyDeliver = isAdmin() && proxyDeliverTargets.length > 0;

  const courtesySelectedCoupons = useMemo(
    () =>
      Array.from(state.courtesySelectedIds)
        .map((id) => couponMap.get(id))
        .filter((c): c is GiftCouponSummary => !!c),
    [state.courtesySelectedIds, couponMap],
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-[99vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <CouponDetailHeader
          stats={state.stats}
          orderId={props.orderId}
          order={props.order}
          previewCopied={state.previewCopied}
          setPreviewCopied={state.setPreviewCopied}
          onClose={onClose}
          canSendUsageGuide={canSendUsageGuide}
          onSendUsageGuide={() => setShowUsageGuideModal(true)}
          canExportDeliveredPhones={canExportDeliveredPhones}
          onExportDeliveredPhones={() =>
            exportDeliveredCouponPhoneExcel(props.order, sortedCoupons)
          }
          canExportPhoneForm={canExportPhoneForm}
          onExportPhoneForm={() =>
            exportDeliveredCouponPhoneExcel(props.order, sortedCoupons, {
              includeUnsent: true,
            })
          }
          canUploadPhoneForm={canUploadPhoneForm}
          onUploadPhoneForm={() => phoneFormFileRef.current?.click()}
          canProxyDeliver={canProxyDeliver}
          onProxyDeliver={() => setShowProxyDeliverModal(true)}
        />

        <ResultBanners
          cancelResult={state.cancelResult}
          additionalResult={state.additionalResult}
          disposeResult={state.disposeResult}
          refundResult={state.refundResult}
        />

        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
          <CrossCouponOverlapBanner
            overlap={state.crossCouponHashOverlap}
            couponMap={couponMap}
            giftItemEmbedMap={state.giftItemEmbedMap}
            onOpenKakaoShareCallbacks={state.openKakaoCallbackModal}
            onOpenShareHistory={state.openShareHistoryModal}
          />

          <div className="mb-3 grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
            {state.canCancel && state.cancellableCoupons.length > 0 && (
              <CancelSection
                cancellableCoupons={state.cancellableCoupons}
                selectedIds={state.selectedIds}
                isFullCancel={state.isFullCancel}
                isManualRefund={state.isManualRefund}
                giftItemEmbedMap={state.giftItemEmbedMap}
                toggleSelectAll={state.toggleSelectAll}
                onRequestCancel={state.openCancelFlow}
              />
            )}

            {state.canCancel && state.reissueStatsByItem.length > 0 && (
              <ReissueSection
                reissueStatsByItem={state.reissueStatsByItem}
                totalReissueRemaining={state.totalReissueRemaining}
                showAdditionalForm={state.showAdditionalForm}
                setShowAdditionalForm={state.setShowAdditionalForm}
                reissueGiftItemId={state.reissueGiftItemId}
                setReissueGiftItemId={state.setReissueGiftItemId}
                additionalCount={state.additionalCount}
                setAdditionalCount={state.setAdditionalCount}
                selectedReissueStat={state.selectedReissueStat}
                isCreatingAdditional={state.isCreatingAdditional}
                handleCreateAdditional={state.handleCreateAdditional}
                reissueOverride={state.reissueOverride}
                requestReissueOverride={state.requestReissueOverride}
              />
            )}

            {state.canCancel && (
              <DisposeSection
                disposeMode={state.disposeMode}
                setDisposeMode={state.setDisposeMode}
                disposeAction={state.disposeAction}
                setDisposeAction={state.setDisposeAction}
                disposeSelectedIds={state.disposeSelectedIds}
                setDisposeSelectedIds={state.setDisposeSelectedIds}
                setShowDisposeConfirm={state.setShowDisposeConfirm}
                disposableCoupons={state.disposableCoupons}
                toggleDisposeSelectAll={state.toggleDisposeSelectAll}
              />
            )}

            {isAdmin() && props.orderId && (
              <BillingCardSection orderId={props.orderId} />
            )}

            {canSendCourtesy && state.courtesyEligibleCoupons.length > 0 && (
              <CourtesySection
                courtesyMode={state.courtesyMode}
                setCourtesyMode={state.setCourtesyMode}
                courtesyEligibleCoupons={state.courtesyEligibleCoupons}
                courtesySelectedIds={state.courtesySelectedIds}
                setCourtesySelectedIds={state.setCourtesySelectedIds}
                toggleCourtesySelectAll={state.toggleCourtesySelectAll}
                setShowCourtesyModal={state.setShowCourtesyModal}
              />
            )}
          </div>

          <CouponTable
            sortedCoupons={state.sortedCoupons}
            giftItemEmbedMap={state.giftItemEmbedMap}
            canCancel={state.canCancel}
            hasCancellableCoupons={state.cancellableCoupons.length > 0}
            disposeMode={state.disposeMode}
            courtesyMode={state.courtesyMode}
            selectedIds={state.selectedIds}
            disposeSelectedIds={state.disposeSelectedIds}
            courtesySelectedIds={state.courtesySelectedIds}
            isPromoting={state.isPromoting}
            crossCouponOverlapIds={state.crossCouponHashOverlap.overlapCouponIds}
            toggleSelect={state.toggleSelect}
            toggleDisposeSelect={state.toggleDisposeSelect}
            toggleCourtesySelect={state.toggleCourtesySelect}
            onOpenShareHistory={state.openShareHistoryModal}
            handlePromoteToReissued={state.handlePromoteToReissued}
            onIssueRefund={state.handleIssueRefund}
            onIssueRenewal={state.handleIssueRenewal}
            onCancelIssue={state.handleCancelIssue}
            onOpenKakaoShareCallbacks={state.openKakaoCallbackModal}
            isIssuing={state.isIssuing}
            onResetShareStatus={state.handleResetShareStatus}
            isResettingShare={state.isResettingShare}
            onUpdateAdditionalPhone={state.handleUpdateAdditionalPhone}
            onOpenExtraRedeem={state.openExtraRedeemModal}
            onSpawnPay={state.openSpawnPayModal}
            onSpawnFulfill={state.openSpawnFulfillModal}
            onSpawnCancel={state.handleCancelSpawn}
            cancelingSpawnId={state.cancelingSpawnId}
          />
        </div>

        {state.showDisposeConfirm && (
          <DisposeConfirmDialog
            disposeAction={state.disposeAction}
            disposeSelectedIds={state.disposeSelectedIds}
            disposeReason={state.disposeReason}
            setDisposeReason={state.setDisposeReason}
            isDisposing={state.isDisposing}
            setShowDisposeConfirm={state.setShowDisposeConfirm}
            handleDispose={state.handleDispose}
          />
        )}

        {state.showCancelConfirm && (
          <CancelConfirmDialog
            isFullCancel={state.isFullCancel}
            hasDeliveryItems={state.hasDeliveryItems}
            selectedIds={state.selectedIds}
            cancelReason={state.cancelReason}
            setCancelReason={state.setCancelReason}
            isCancelling={state.isCancelling}
            setShowCancelConfirm={state.setShowCancelConfirm}
            handleCancelRequest={state.handleCancelRequest}
          />
        )}

        {state.showManualRefundModal && (
          <ManualRefundCancelDialog
            isFullCancel={state.isFullCancel}
            selectedCount={state.selectedIds.size}
            method={props.order?.paymentDetail?.method}
            expectedRefundAmount={state.expectedRefundAmount}
            isCancelling={state.isCancelling}
            onClose={() => state.setShowManualRefundModal(false)}
            onConfirm={state.handleManualRefundCancel}
          />
        )}
      </div>

      {state.showCourtesyModal && (
        <CourtesyMessageModal
          coupons={courtesySelectedCoupons}
          onClose={() => state.setShowCourtesyModal(false)}
          onSent={() => {
            state.setShowCourtesyModal(false);
            state.setCourtesyMode(false);
            state.setCourtesySelectedIds(new Set());
          }}
        />
      )}

      {showUsageGuideModal && props.orderId && (
        <UsageGuideSendModal
          giftOrderId={props.orderId}
          targets={usageGuideTargets}
          onClose={() => setShowUsageGuideModal(false)}
        />
      )}

      {showProxyDeliverModal && (
        <ProxyDeliverModal
          targets={proxyDeliverTargets}
          orderCoupons={sortedCoupons}
          giftItemEmbedMap={state.giftItemEmbedMap}
          order={props.order}
          onClose={() => setShowProxyDeliverModal(false)}
        />
      )}

      {/* 기입폼 업로드 파일 선택 (숨김) — 같은 파일 재선택 가능하게 value 초기화 */}
      <input
        ref={phoneFormFileRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handlePhoneFormFile(file);
        }}
      />

      {phoneFormParsed && (
        <PhoneFormUploadModal
          parsed={phoneFormParsed}
          giftItemEmbedMap={state.giftItemEmbedMap}
          onClose={(didApply) => {
            setPhoneFormParsed(null);
            // 쿠폰 목록은 부모(주문 목록)에서 내려오는 스냅샷이라 저장분 반영은 새로고침으로.
            if (didApply) window.location.reload();
          }}
        />
      )}

      {state.kakaoCallbackCoupon && (
        <KakaoShareCallbackModal
          coupon={state.kakaoCallbackCoupon}
          onClose={state.closeKakaoCallbackModal}
        />
      )}

      {state.shareHistoryCoupon && (
        <CouponShareHistoryModal
          coupon={state.shareHistoryCoupon}
          onClose={state.closeShareHistoryModal}
        />
      )}

      {state.extraRedeemCoupon && (
        <ExtraRedeemModal
          coupon={state.extraRedeemCoupon}
          onClose={state.closeExtraRedeemModal}
          onChanged={() => window.location.reload()}
        />
      )}

      {state.spawnPayCoupon && (
        <ExtraPayModal
          couponId={state.spawnPayCoupon._id}
          couponCode={state.spawnPayCoupon.code}
          productLabel={
            state.giftItemEmbedMap.get(state.spawnPayCoupon.giftItem)?.name ??
            state.spawnPayCoupon.code
          }
          onPay={state.handleSpawnPay}
          onClose={state.closeSpawnPayModal}
        />
      )}

      {state.spawnFulfillCoupon && (
        <ExtraFulfillModal
          couponId={state.spawnFulfillCoupon._id}
          couponCode={state.spawnFulfillCoupon.code}
          productLabel={
            state.giftItemEmbedMap.get(state.spawnFulfillCoupon.giftItem)?.name ??
            state.spawnFulfillCoupon.code
          }
          getDonors={state.getDonorCandidates}
          onFulfill={state.handleSpawnFulfill}
          onClose={state.closeSpawnFulfillModal}
        />
      )}
    </div>
  );
};
